/* eslint-disable no-use-before-define */
/* eslint-disable no-useless-escape */
import { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dialog, TextArea } from 'uxd-design-system-components';
import CommentModalProps from '~types/components/CommentModalProps';
import './CommentModal.scss';
import LocaleContext from '../../contexts/LocaleContext';
import getText from '../../assets/locale/locale';

const CommentModal = ({
  displayCommentModal,
  setDisplayCommentModal,
  setUserComments,
  contactName,
  emailResultsToFA,
  faInfo,
}: CommentModalProps) => {
  const locId = useContext(LocaleContext);
  const commentRegex = /[`~\*\^\[\]{}]/;
  const MAX_LENGTH = 500;
  const faFullName = `${faInfo.faFirstName} ${faInfo.faLastName}`;
  const defaultText = getText(locId, 'defaultText', 'commentModal');
  const faHeadshotImage = faInfo.faPhoto;
  const invalidComment =
    getText(locId, 'commentError', 'commentModal') +
    getText(locId, 'invalidChars', 'commentModal');

  const [isShareButtonDisabled, setIsShareButtonDisabled] = useState(false);
  const [commentError, setCommentError] = useState(null);
  const [subText, setSubText] = useState(
    getText(locId, 'paragraphOneText', 'commentModal').replace(
      '%MAX_LENGTH%',
      MAX_LENGTH.toString()
    )
  );
  const [charsLeft, setCharsLeft] = useState(MAX_LENGTH);
  const [localComment, setLocalComment] = useState('');

  const buttons = [
    {
      text: getText(locId, 'cancelButton', 'commentModal'),
      onClick: () => setDisplayCommentModal(false),
      styleVariant: 'secondary' as const,
    },
    {
      text: getText(locId, 'shareResultsButton', 'contactFormModal'),
      onClick: () => shareButtonHandler(),
      styleVariant: 'primary' as const,
      disabled: isShareButtonDisabled,
    },
  ];

  const validateComment = (input: string) => {
    setLocalComment(input);
    setIsShareButtonDisabled(commentRegex.test(input));
    setCommentError(commentRegex.test(input) ? invalidComment : null);
  };

  const headerTextHandler = () => {
    const baseHeaderText = getText(locId, 'header', 'commentModal');
    let headerText;
    if (faInfo.faFirstName && faInfo.faLastName) {
      headerText = baseHeaderText
        .replace('%CONTACT_FIRSTNAME%', contactName.firstName)
        .replace('%FA_NAME%', faFullName);
    } else {
      headerText = baseHeaderText
        .replace('%CONTACT_FIRSTNAME%', contactName.firstName)
        .replace('%FA_NAME%', defaultText);
    }
    return headerText;
  };

  const shareButtonHandler = () => {
    if (!commentError) {
      setDisplayCommentModal(false);
      emailResultsToFA();
    }
  };

  const commentHandler = (input: string) => {
    validateComment(input);
    setUserComments(input);
    setCharsLeft(MAX_LENGTH - input.length);
  };

  const handleParagraphOneFaText = () => {
    const paragraphOneText = getText(locId, 'paragraphOneText', 'commentModal');
    const faDefaultText = getText(locId, 'defaultText', 'commentModal');
    if (faInfo.faFirstName) {
      return paragraphOneText.replace('%FA_NAME%', faInfo.faFirstName);
    }
    return paragraphOneText.replace(
      '%FA_NAME%',
      faDefaultText.charAt(0).toUpperCase() + faDefaultText.slice(1)
    );
  };

  useEffect(() => {
    if (charsLeft === MAX_LENGTH) {
      setSubText(
        getText(locId, 'subTextOne', 'commentModal').replace(
          '%MAX_LENGTH%',
          MAX_LENGTH.toString()
        )
      );
    } else {
      setSubText(
        getText(locId, 'subTextTwo', 'commentModal').replace(
          '%CHARS_LEFT%',
          charsLeft.toString()
        )
      );
    }
  }, [charsLeft]);

  return (
    <Dialog
      title=""
      open={displayCommentModal}
      buttons={buttons}
      className="commentModal"
      onRequestClose={() => setDisplayCommentModal(false)}
    >
      {' '}
      <div className="title-container">
        <h3>{headerTextHandler()}</h3>
        {faHeadshotImage ? (
          <div className="fa-headshot">
            <img
              src={`data:image/png;base64,${faHeadshotImage}`}
              alt=""
              role="presentation"
              data-testid="fa-headshot"
            />
          </div>
        ) : null}
      </div>
      <div>
        <p className="comment-modal-paragraph">{handleParagraphOneFaText()}</p>
        <p className="comment-modal-paragraph">
          {getText(locId, 'paragraphTwoText', 'commentModal')}
        </p>
      </div>
      <TextArea
        id="user-comment"
        label={getText(locId, 'label', 'commentModal')} // static text used here in-leu of the "optional" prop to allow for lowercase text
        classes="commentModal"
        name="user-comment"
        value={localComment}
        maxLength={MAX_LENGTH}
        microcopy={subText}
        onChange={(text: string) => commentHandler(text)}
        onBlur={validateComment}
        error={commentError}
      />
    </Dialog>
  );
};

CommentModal.propTypes = {
  displayCommentModal: PropTypes.bool.isRequired,
  setDisplayCommentModal: PropTypes.func.isRequired,
  setUserComments: PropTypes.func.isRequired,
  contactName: PropTypes.objectOf(PropTypes.string).isRequired,
  emailResultsToFA: PropTypes.func.isRequired,
  faInfo: PropTypes.objectOf(PropTypes.string).isRequired,
};

export default CommentModal;
